/**
 * Season wiring — turns schedule.js into the dated sessions the screens read.
 *
 * schedule.js is the source of truth for the weekly pattern, capacity and the
 * generator. Nothing here modifies it. What lives here is the configuration the
 * generator takes as arguments, kept separate precisely because it is the part
 * that is not settled: the holiday calendar is provisional, so closures are
 * passed in rather than baked into the generator.
 */

import { CAPACITY, generateSeason } from './schedule';
import { addDaysISO } from './calendar';
import { ROTATIONS } from '../tokens';

/**
 * PROVISIONAL — the 2026-27 calendar is not final.
 *
 * From the Blueprint's closure table. Half-days before a holiday count as
 * closed: the handbook closes at noon and the first block is 3:00 PM, so
 * nothing runs anyway.
 *
 * Passed to generateSeason as an argument. When the real calendar lands, this
 * constant changes and nothing else does.
 */
export const HOLIDAY_CLOSURES_2026_27 = [
  // Thanksgiving Break — Nov 25 (noon) to Nov 29. The handbook closes at noon and
  // the first block is 3:00 PM, so a half-day is a closed day here.
  '2026-11-25', '2026-11-26', '2026-11-27', '2026-11-28', '2026-11-29',
  // Christmas & New Year Break — Dec 23 (noon) to Jan 3.
  '2026-12-23', '2026-12-24', '2026-12-25', '2026-12-26', '2026-12-27',
  '2026-12-28', '2026-12-29', '2026-12-30', '2026-12-31',
  '2027-01-01', '2027-01-02', '2027-01-03',
  // Presidents' Day.
  '2027-02-15',
];

/**
 * PROVISIONAL — the tournaments that run on closed days.
 *
 * Three of the closures above are closed to classes but open to tournament
 * competitors: the Post-Thanksgiving tournament (Nov 27-28), the Holiday
 * tournament (Dec 28-29), and Presidents' Day (Feb 15). Two of those fall on
 * weekdays, which carry no tournament block in the weekly pattern, so they
 * cannot be expressed as a closure exception — they are explicitly dated
 * sessions layered on top.
 *
 * Times are a working assumption. The Blueprint gives the dates but not the
 * format, and a holiday tournament may well run longer than a Saturday block.
 * Confirm before these reach a family.
 */
export const HOLIDAY_TOURNAMENTS_2026_27 = [
  { date: '2026-11-27', time: '10:30 AM', type: 'tournament', label: 'Post-Thanksgiving Tournament' },
  { date: '2026-11-28', time: '10:30 AM', type: 'tournament', label: 'Post-Thanksgiving Tournament' },
  { date: '2026-12-28', time: '10:30 AM', type: 'tournament', label: 'Holiday Tournament' },
  { date: '2026-12-29', time: '10:30 AM', type: 'tournament', label: 'Holiday Tournament' },
  { date: '2027-02-15', time: '10:30 AM', type: 'tournament', label: "Presidents' Day Tournament" },
];

/**
 * PROVISIONAL — but anchored to the handbook rather than invented.
 *
 * The Annual Operating Cycle runs Season Startup in October, In-Season November
 * through January, Wind Down in February, and teardown in March. Sessions
 * therefore run early November to late February. The bays are disassembled and
 * in the garage from March, so generating past that would schedule athletes into
 * an empty room.
 *
 * Ends on a Saturday so the final week is whole. 25/26 ran 212 sessions across
 * 17 weeks, which is the number to sanity-check against.
 *
 * Nov 2 is set-up day (owner ruling 2026-09-28); sessions start Nov 3.
 */
export const SEASON_BOUNDS = { start: '2026-11-03', end: '2027-02-27' };

/**
 * @param {object} [opts]
 * @param {string[]} [opts.closures]  Defaults to the provisional calendar above.
 * @param {Array} [opts.extras]       Defaults to the holiday tournaments above.
 */
export function buildSeason({
  closures = HOLIDAY_CLOSURES_2026_27,
  extras = HOLIDAY_TOURNAMENTS_2026_27,
} = {}) {
  return generateSeason({ ...SEASON_BOUNDS, closures, extras });
}

/**
 * Built once at module load. The generator is deterministic, so rebuilding it
 * per render would burn work to produce an identical array — and hand the hook
 * seam a new array identity every time.
 */
export const SEASON = buildSeason();

export const SCAFFOLD_TODAY = '2027-02-18';

/** "2027-02-20" -> { date: "20", dow: "Sat" }, for the booking date strip. */
export function datePill(iso) {
  const d = new Date(iso + 'T00:00:00Z');
  return {
    iso,
    date: String(d.getUTCDate()),
    dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()],
  };
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2027-02-20" -> "Saturday, Feb 20" — or "Today" when it is. */
export function dayLabel(iso, today) {
  if (iso === today) return 'Today';
  const d = new Date(iso + 'T00:00:00Z');
  return `${DAY_NAMES[d.getUTCDay()]}, ${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/**
 * Sessions grouped by date, built once beside SEASON. The season is sorted, so
 * the map's key order is chronological — `upcomingDates` walks keys instead of
 * rescanning all ~240 sessions per date per render.
 */
export const SEASON_BY_DATE = SEASON.reduce((map, s) => {
  const list = map.get(s.date);
  if (list) list.push(s);
  else map.set(s.date, [s]);
  return map;
}, new Map());

/**
 * Resolve a booking reference { date, block } against the season. Returns null
 * for a reference pointing at a closure or an out-of-range block, so a stale
 * booking cannot invent a session the schedule does not have.
 */
export function resolveBooking(ref) {
  const onDate = SEASON_BY_DATE.get(ref.date);
  return (onDate && onDate[ref.block]) || null;
}

const CLOSED = new Set(HOLIDAY_CLOSURES_2026_27);

/**
 * The first week of a weekly slot, from `iso` on, that the season actually
 * runs - for "Next {weekday} opens ..." and "come back for {date}" copy, where
 * a closure (the Dec 23 - Jan 3 break sits right past the Nov 1-anchored
 * window) has nothing to come back for. A closed date still counts when a
 * dated extra of the same time and type runs on it (the holiday tournaments).
 * null once past the season's end. Live sessions are provisioned from
 * buildSeason(), so the same closure list holds for them.
 */
export function firstRunningWeek(iso, time, type = 'training') {
  for (let d = iso; d <= SEASON_BOUNDS.end; d = addDaysISO(d, 7)) {
    if (weekRuns(d, time, type)) return d;
  }
  return null;
}

/**
 * The last week of a weekly slot, from `iso` through `untilISO` (clamped to
 * the season's end), that the season actually runs - the date a repeat can
 * really reach when the booking window ends inside a closure (a window that
 * ends Dec 31 holds nothing past Dec 22: the Dec 23 - Jan 3 break). Same
 * dated-extra rule as firstRunningWeek. null when no week in the range runs.
 */
export function lastRunningWeek(iso, untilISO, time, type = 'training') {
  const last = untilISO < SEASON_BOUNDS.end ? untilISO : SEASON_BOUNDS.end;
  let found = null;
  for (let d = iso; d <= last; d = addDaysISO(d, 7)) {
    if (weekRuns(d, time, type)) found = d;
  }
  return found;
}

/** A date runs for a slot unless it is closed with no dated extra of that time and type. */
function weekRuns(d, time, type) {
  return !CLOSED.has(d) || (SEASON_BY_DATE.get(d) ?? []).some((s) => s.time === time && s.type === type);
}

/**
 * OPEN — which rotation runs in which block is not specified anywhere.
 *
 * The handoff says sessions are labelled by their Workshop / Lab / Arena
 * rotation, and the artboards show the same block time carrying different
 * rotations on different days, so it genuinely rotates rather than being fixed
 * per time slot. The cycle below is a placeholder that produces a stable,
 * plausible label; it is not a scheduling decision. Replace it when the real
 * rotation assignment exists — every caller goes through this one function.
 */
export function rotationFor(session) {
  const day = new Date(session.date + 'T00:00:00Z').getUTCDate();
  const block = Number(String(session.id).split('-').pop()) || 0;
  return ROTATIONS[(day + block) % ROTATIONS.length];
}

/** Sessions on one date, in block order. */
export function sessionsForDate(sessions, date) {
  return sessions.filter((s) => s.date === date);
}

/** The next `count` dates that actually have sessions, from `from` onward. */
export function upcomingDates(sessions, from, count = 7) {
  const seen = [];
  for (const s of sessions) {
    if (s.date < from) continue;
    if (!seen.includes(s.date)) seen.push(s.date);
    if (seen.length === count) break;
  }
  return seen;
}

/**
 * Capacity as the booking list shows it. `session.capacity` is a plain number
 * on every session doc, so this needs no per-type branch - the per-type rule
 * (training 14, tournament 25, owner 2026-09-18) is applied where sessions
 * are MADE (schedule.js CAPACITY_BY_TYPE, the calendar sync's map), never
 * where they are read.
 */
export function capacityFor(session) {
  const left = Math.max(0, session.capacity - session.booked);
  if (left === 0) return { state: 'full', label: 'Full' };
  return { state: 'available', label: `${left} left` };
}

export { CAPACITY };
